import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO, subDays } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { BILLCOM_PO_BALANCE_RECONCILIATION } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const PAGE_SIZE = 50;
const STATUSES = [
  'bill_without_po',
  'po_not_billed',
  'over_billed',
  'under_billed',
  'paid_mismatch',
  'balance_mismatch',
  'scheduled_pending',
  'balanced',
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const formatStatus = (value) => value === 'scheduled_pending'
  ? 'Scheduled in Bill.com'
  : String(value || '').replace(/_/g, ' ');
const formatValue = (value, column) => {
  if (value === null || value === undefined || value === '') return '—';
  if (column.type === 'currency') return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  if (column.type === 'date') {
    const parsed = parseISO(String(value).slice(0, 10));
    return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'MM/dd/yyyy');
  }
  if (column.key === 'status') return formatStatus(value);
  return value;
};

const BillcomPoBalanceReconciliationReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const defaultEnd = format(new Date(), 'yyyy-MM-dd');
  const defaultStart = format(subDays(new Date(), 364), 'yyyy-MM-dd');
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultStart, defaultEnd);
  const [status, setStatus] = useState('all');
  const [vendor, setVendor] = useState('');
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totals, setTotals] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resolutionSaving, setResolutionSaving] = useState(null);
  const [resolutionError, setResolutionError] = useState(null);

  const params = useCallback((includePage = true) => {
    const query = new URLSearchParams({ startDate, endDate });
    if (includePage) {
      query.set('page', page);
      query.set('pageSize', PAGE_SIZE);
    }
    if (status !== 'all') query.set('status', status);
    if (vendor) query.set('vendor', vendor);
    return query;
  }, [startDate, endDate, page, status, vendor]);

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/reports/${BILLCOM_PO_BALANCE_RECONCILIATION}?${params()}`, {
        headers: authHeaders(),
        signal,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setColumns(json.columns || []);
      setTotals(json.totals || {});
      setTotalRecords(json.totalRecords || 0);
      setTotalPages(json.totalPages || 1);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
      setData([]);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, params]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  useEffect(() => setPage(1), [startDate, endDate, status, vendor]);

  const exportData = async () => {
    const response = await fetch(`/api/v1/reports/${BILLCOM_PO_BALANCE_RECONCILIATION}/export?${params(false)}`, { headers: authHeaders() });
    if (!response.ok) throw new Error('Failed to export report');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'billcom-po-balance-reconciliation.csv';
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const saveResolution = async (row, value) => {
    const resolution = value.trim() || null;
    if (resolution === (row.resolution || null)) return;
    setResolutionSaving(row.po_number);
    setResolutionError(null);
    try {
      const response = await fetch('/api/v1/reports/billcom-po-balance-reconciliation/resolution', {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: row.po_number, resolution }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to save resolution');
      setData((current) => current.map((item) => (
        item === row ? { ...item, resolution: json.data?.resolution ?? resolution } : item
      )));
    } catch (saveError) {
      setResolutionError(saveError.message);
    } finally {
      setResolutionSaving(null);
    }
  };

  const renderCell = (row, column) => {
    if (column.key === 'po_number' && row.eadin_po_ids?.length) {
      return (
        <Link
          to={`${ROUTES.PURCHASE_ORDERS}/${row.eadin_po_ids[0]}`}
          className="text-blue-700 hover:underline"
        >
          {row.po_number}
        </Link>
      );
    }
    if (column.key === 'billcom_invoices') {
      if (!row.billcom_bills?.length) return formatValue(row[column.key], column);
      return (
        <span className="flex gap-2">
          {row.billcom_bills.map((bill) => (
            <a key={bill.bill_id || bill.invoice_number} href={bill.url || '#'} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
              {bill.invoice_number || bill.bill_id || 'Bill'}
            </a>
          ))}
        </span>
      );
    }
    if (column.key === 'vendor' && row.billcom_vendor_url) {
      return <a href={row.billcom_vendor_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">{row.vendor}</a>;
    }
    if (column.key === 'eadin_po_count' && row.eadin_po_count > 1) {
      return <span title={`Duplicate POs: ${(row.eadin_po_ids || []).join(', ')}`}>{row.eadin_po_count} <span className="text-gray-500">(duplicates)</span></span>;
    }
    if (column.key === 'resolution') {
      return (
        <div className="min-w-[220px]">
          <textarea
            className="w-full rounded border px-2 py-1 text-xs"
            defaultValue={row.resolution || ''}
            rows={2}
            onBlur={(event) => saveResolution(row, event.target.value)}
          />
          {resolutionSaving === row.po_number && <span className="text-[10px] text-gray-500">Saving…</span>}
        </div>
      );
    }
    return formatValue(row[column.key], column);
  };

  const filters = (
    <div className="contents">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onDateChange={({ startDate: start, endDate: end }) => {
          setDateRange({ startDate: start, endDate: end });
          setPage(1);
        }}
        summaryDateFormat="MM/dd/yyyy"
        pickerDateFormat="MM/dd/yyyy"
        label="PO date range"
      />
      <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
        <option value="all">All statuses</option>
        {STATUSES.map((value) => <option key={value} value={value}>{formatStatus(value)}</option>)}
      </select>
      <input
        className="rounded border px-3 py-2 text-sm"
        placeholder="Exact vendor name"
        value={vendor}
        onChange={(event) => setVendor(event.target.value)}
      />
    </div>
  );

  const pagination = (
    <div className="flex items-center justify-between text-sm">
      <span>{totalRecords.toLocaleString()} rows</span>
      <div className="flex gap-2">
        <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button>
        <span className="py-1">Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button>
      </div>
    </div>
  );

  return (
    <ReportTemplate
      reportId={BILLCOM_PO_BALANCE_RECONCILIATION}
      title="Bill.com PO Balance Reconciliation"
      description="Tie out Eadin PO balances due against Bill.com bill balances, per PO."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={data} columns={columns} filename="billcom-po-balance-reconciliation" onExport={exportData} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="space-y-3">
        <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
          {[
            ['eadin_balance', 'Eadin balance due'],
            ['billcom_due', 'Bill.com due (PO)'],
            ['billcom_scheduled', 'Bill.com scheduled'],
            ['non_po_billcom_due', 'Bill.com due (non-PO)'],
            ['balance_diff', 'Difference'],
          ].map(([key, label]) => (
            <div key={key} className="rounded bg-gray-50 p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="font-semibold">{formatValue(totals[key], { type: 'currency' })}</div>
            </div>
          ))}
        </div>
        {resolutionError && <div className="text-xs text-red-600">{resolutionError}</div>}
        <div className="flex flex-wrap gap-2 text-xs">
          {STATUSES.map((value) => <span key={value} className="rounded bg-gray-100 px-2 py-1">{formatStatus(value)}: {totals[value] || 0}</span>)}
        </div>
        <div className="overflow-auto rounded border bg-white">
          {data.length === 0 ? <div className="px-6 py-12 text-center text-sm text-gray-500">No Bill.com PO balance rows in the selected range.</div> : (
            <table className="min-w-max text-left text-xs">
              <thead className="sticky top-0 bg-gray-100"><tr>{columns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>)}</tr></thead>
              <tbody>{data.map((row, index) => <tr key={`${row.po_number || row.vendor}-${index}`} className={index % 2 ? 'bg-gray-50' : ''}>
                {columns.map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2">{renderCell(row, column)}</td>)}
              </tr>)}</tbody>
            </table>
          )}
        </div>
      </div>
    </ReportTemplate>
  );
};

export default BillcomPoBalanceReconciliationReport;
